import React, { useEffect, useState } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Typography,
    Button,
    LinearProgress,
    TextField,
    IconButton,
    Pagination, CircularProgress,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';

interface CoursesListProps {
    courses: any[];
    loading: boolean;
    search: string;
    showSearchField?: boolean;
    currentPage: number;
    pageSize: number;
    totalItems: number;
    onPageChange: (page: number) => void;
    onSearchChange?: (value: string) => void;
    onSearchSubmit?: (value: string) => void;
}

const CoursesList = ({ 
    courses, 
    loading, 
    search, 
    showSearchField = true,
    currentPage,
    pageSize,
    totalItems,
    onPageChange,
    onSearchChange,
    onSearchSubmit
}: CoursesListProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [localSearch, setLocalSearch] = useState(search);

    useEffect(() => {
        setLocalSearch(search);
    }, [search]);

    const totalPages = Math.ceil(totalItems / pageSize);

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        onPageChange(value);
    };

    const handlePrevNext = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            onPageChange(newPage);
        }
    };

    const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && onSearchSubmit && localSearch.trim() !== '') {
            onSearchSubmit(localSearch);
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalSearch(e.target.value);
        if (onSearchChange) {
            onSearchChange(e.target.value);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {showSearchField && (
                <Box sx={{ 
                    position: 'relative',
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center'
                }}>
                    <SearchIcon 
                        sx={{
                            position: 'absolute',
                            left: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#757575',
                            fontSize: '20px',
                            zIndex: 1
                        }}
                    />
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search by Course Name, Course ID"
                        value={localSearch}
                        onChange={handleSearchChange}
                        onKeyPress={handleSearchKeyPress}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                backgroundColor: '#fff',
                                '& fieldset': {
                                    borderColor: '#e0e0e0',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#1976d2',
                                },
                            },
                            '& .MuiOutlinedInput-input': {
                                paddingLeft: '40px',
                            }
                        }}
                    />
                </Box>
            )}
            
            <Box sx={{ position: 'relative', mb: 3 }}>
                {currentPage > 1 && (
                    <IconButton 
                        sx={{ 
                            position: 'absolute', 
                            left: -20, 
                            top: '50%', 
                            transform: 'translateY(-50%)',
                            bgcolor: 'background.paper',
                            boxShadow: 2,
                            '&:hover': { bgcolor: 'background.paper' }
                        }}
                        onClick={() => handlePrevNext(currentPage - 1)}
                    >
                        <ChevronLeft />
                    </IconButton>
                )}

                <Grid container spacing={3}>
                    {courses.map((course) => (
                        <Grid item xs={12} sm={6} md={3} key={course.id}>
                            <Card sx={{ 
                                height: '100%', 
                                display: 'flex', 
                                flexDirection: 'column',
                                bgcolor: '#f8f9fa',
                                borderRadius: '8px',
                                boxShadow: 'none',
                                border: '1px solid #e0e0e0'
                            }}>
                                <CardMedia
                                    component="img"
                                    height="180"
                                    image={course.imageUrl || '/default-course-image.jpg'}
                                    alt={course.title}
                                    sx={{ 
                                        objectFit: 'cover',
                                        borderBottom: '1px solid #e0e0e0',
                                        borderRadius: '8px',
                                        width: '100%'
                                    }}
                                />
                                <CardContent sx={{ 
                                    flexGrow: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    p: 3,
                                }}>
                                    <Typography 
                                        variant="h6" 
                                        sx={{ 
                                            mb: 2,
                                            fontSize: '16px',
                                            fontWeight: 600,
                                            lineHeight: '24px',
                                            height: '48px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                        }}
                                    >
                                        {course.title}
                                    </Typography>
                                    <Box sx={{ mt: 'auto' }}>
                                        <LinearProgress 
                                            variant="determinate" 
                                            value={course.completionPercentage}
                                            sx={{
                                                height: 6,
                                                bgcolor: '#e9ecef',
                                                borderRadius: 3,
                                                '& .MuiLinearProgress-bar': {
                                                    bgcolor: '#198754',
                                                    borderRadius: 3,
                                                }
                                            }}
                                        />
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                mt: 1,
                                                color: '#6C757D',
                                                fontSize: '14px',
                                                lineHeight: '20px'
                                            }}
                                        >
                                            {course.completedActivities} out of {course.totalActivities} activities completed
                                        </Typography>
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                color: course.completionPercentage === 100 ? '#198754' : '#6C757D',
                                                fontSize: '14px',
                                                lineHeight: '20px',
                                                mb: 2
                                            }}
                                        >
                                            {course.completionPercentage}% Course Completed
                                        </Typography>
                                        <Button 
                                            variant="contained" 
                                            fullWidth 
                                            href={course.viewUrl}
                                            target="_blank"
                                            sx={{ 
                                                bgcolor: '#FF4D00',
                                                '&:hover': {
                                                    bgcolor: '#cc3d00'
                                                },
                                                height: '40px',
                                                textTransform: 'none',
                                                borderRadius: '4px',
                                                fontSize: '14px',
                                                fontWeight: 500
                                            }}
                                        >
                                            Go
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {currentPage < totalPages && (
                    <IconButton 
                        sx={{ 
                            position: 'absolute', 
                            right: -20, 
                            top: '50%', 
                            transform: 'translateY(-50%)',
                            bgcolor: 'background.paper',
                            boxShadow: 2,
                            '&:hover': { bgcolor: 'background.paper' }
                        }}
                        onClick={() => handlePrevNext(currentPage + 1)}
                    >
                        <ChevronRight />
                    </IconButton>
                )}
            </Box>

            {/* Phân trang số ở dưới */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Pagination 
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    shape="rounded"
                />
            </Box>
        </Box>
    );
};

export default CoursesList; 